import { SHOW_SIGNUP } from "tools/config";
import { dataService } from "tools/dataService/dataService";
import UserContextInfo from "tools/userContextInfo";
import { updateActivePlatformLS } from "tools/commonFunctions";
import * as BackEndConsts from "tools/constants/backEndConstants";
import { DEPLOYMENT_STATE_NEVERDEPLOYED } from "tools/constants/backEndConstants";
import i18n from "localization/i18n";

/**
 * Sample apps deployed automatically in local DEV to cover common UI scenarios.
 * Other platform apps remain available but undeployed (never-deployed flows).
 */
const DEV_SAMPLE_APPS = [
  {
    serviceName: "dataflow",
    appName: "airflow",
    launchingKey: "kubernetesPlatform.devProvision.launchingAirflow"
  },
  {
    serviceName: "datamesh",
    appName: "denodo",
    launchingKey: "kubernetesPlatform.devProvision.launchingDenodo"
  }
];

/**
 * Dev-only rapid provisioning orchestrator.
 * Uses existing GraphQL queries/mutations to create a demo Kubernetes platform
 * when none exists for the active company. Guarded by local env detection.
 */
const devProvision = {
  isLocalEnv() {
    return SHOW_SIGNUP === true;
  },

  findServiceApp(servicesData, serviceName, appName) {
    if (!servicesData || !Array.isArray(servicesData)) {
      return null;
    }

    const normalizedService = String(serviceName || "").toLowerCase();
    const normalizedApp = String(appName || "").toLowerCase();
    const service = servicesData.find(
      (entry) => entry && String(entry.name || "").toLowerCase() === normalizedService
    );

    if (!service || !Array.isArray(service.companyserviceapps)) {
      return null;
    }

    return service.companyserviceapps.find(
      (app) => app && String(app.name || "").toLowerCase() === normalizedApp
    ) || null;
  },

  resolveLocalDevPlatform(platforms) {
    if (!platforms) {
      return null;
    }

    const list = Array.isArray(platforms) ? platforms : [platforms];
    return list.find((platform) => platform && platform.name === "LOCAL-DEV" && !platform.demo)
      || list.find((platform) => platform && platform.name === "LOCAL-DEV")
      || null;
  },

  async getLocalDevPlatform(companyId) {
    const result = await dataService.queries.getKubernetesNameAndState(companyId);
    return this.resolveLocalDevPlatform(result);
  },

  isSampleAppUndeployed(app) {
    if (!app || !app.id) {
      return true;
    }

    if (app.enabled === false) {
      return true;
    }

    return String(app.state || "") === DEPLOYMENT_STATE_NEVERDEPLOYED;
  },

  async needsSampleAppsDeploy(companyId) {
    const platform = await this.getLocalDevPlatform(companyId);
    if (!platform || !platform.id) {
      return false;
    }

    const servicesData = await dataService.queries.getPlatformServicesData(platform.id);
    return DEV_SAMPLE_APPS.some((sample) => {
      const app = this.findServiceApp(servicesData, sample.serviceName, sample.appName);
      return this.isSampleAppUndeployed(app);
    });
  },

  async getProvisionMode(companyId) {
    if (!companyId || companyId === "No Company ID!") {
      return null;
    }

    const exists = await this.hasExistingPlatform(companyId);
    if (!exists) {
      return "full";
    }

    const needsSamples = await this.needsSampleAppsDeploy(companyId);
    return needsSamples ? "samples" : null;
  },

  async ensureComponentsStorage(kubernetesId, onProgress) {
    const notify = (msg) => { if (onProgress) onProgress(msg); };
    const platformData = await dataService.queries.getKubernetesDeployInfo(kubernetesId);
    const platform = platformData && platformData.kubernetesplatform
      ? platformData.kubernetesplatform
      : null;

    if (!platform || !Array.isArray(platform.k8labels) || platform.k8labels.length === 0) {
      console.warn("RapidProvision: No k8 labels found to initialize component storage");
      return false;
    }

    const defaultLabel = platform.k8labels.find((label) => label.default) || platform.k8labels[0];
    notify(i18n.t("kubernetesPlatform.devProvision.syncingComponentStorage"));
    const result = await dataService.mutations.newDefaultLabelValue(kubernetesId, defaultLabel.id);
    if (result === BackEndConsts.BACKEND_RESPONSE_ERROR || !result) {
      console.warn("RapidProvision: Could not sync component storage", result);
      return false;
    }

    return true;
  },

  async deployDevSampleApps(kubernetesId, onProgress) {
    const notify = (msg) => { if (onProgress) onProgress(msg); };
    const deployed = [];
    const skipped = [];

    notify(i18n.t("kubernetesPlatform.devProvision.resolvingSampleApps"));
    const servicesData = await dataService.queries.getPlatformServicesData(kubernetesId);

    for (const sample of DEV_SAMPLE_APPS) {
      const app = this.findServiceApp(servicesData, sample.serviceName, sample.appName);
      if (!app || !app.id) {
        skipped.push(sample);
        console.warn(
          `RapidProvision: ${sample.appName} not found in ${sample.serviceName}`
        );
        continue;
      }

      if (!this.isSampleAppUndeployed(app)) {
        skipped.push(sample);
        continue;
      }

      notify(i18n.t(sample.launchingKey));
      const deployResult = await dataService.mutations.deployServiceApp(app.id);
      deployed.push({
        serviceName: sample.serviceName,
        appName: sample.appName,
        appId: app.id,
        result: deployResult
      });
      console.log(
        `RapidProvision: ${sample.appName} deploy triggered, id=`,
        app.id,
        "result=",
        deployResult
      );
    }

    return { deployed, skipped };
  },

  async deploySamplesOnly(companyIdParam, onProgress) {
    const notify = (msg) => { if (onProgress) onProgress(msg); };

    if (!this.isLocalEnv()) {
      return { created: false, skipped: true, reason: "Not local environment" };
    }

    let companyId = companyIdParam;
    if (!companyId || companyId === "No Company ID!") {
      const cid = UserContextInfo.activeCompanyId();
      companyId = cid && cid !== "No Company ID!" ? cid : null;
    }
    if (!companyId) {
      return { created: false, skipped: true, reason: "No active company" };
    }

    const platform = await this.getLocalDevPlatform(companyId);
    if (!platform || !platform.id) {
      return { created: false, error: "LOCAL-DEV platform not found" };
    }

    try {
      notify(i18n.t("kubernetesPlatform.devProvision.activatingServices"));
      await dataService.mutations.activateK8ServicesAndApps(platform.id);
      await this.ensureComponentsStorage(platform.id, onProgress);
      const sampleResult = await this.deployDevSampleApps(platform.id, onProgress);
      return { created: true, mode: "samples", platform, ...sampleResult };
    } catch (e) {
      console.warn("RapidProvision: Error deploying sample apps", e);
      return { created: false, error: "Sample apps deployment failed" };
    }
  },

  async hasExistingPlatform(companyId) {
    try {
      const result = await dataService.queries.getKubernetesNameAndState(companyId);
      if (!result) return false;
      if (Array.isArray(result)) {
        const truePlatforms = result.filter(p => !p.demo);
        return truePlatforms.length > 0;
      }
      return !!result.id && !result.demo;
    } catch (err) {
      // On error, assume no platform to allow manual attempt; UI will handle failures.
      return false;
    }
  },

  /**
   * Run rapid provision flow.
   * - Requires local environment (SHOW_SIGNUP true)
   * - Requires an active company id
   * - Skips if a platform already exists
   */
  async run(companyIdParam, onProgress) {
    const notify = (msg) => { if (onProgress) onProgress(msg); };

    if (!this.isLocalEnv()) {
      return { created: false, skipped: true, reason: "Not local environment" };
    }

    let companyId = companyIdParam;
    if (!companyId || companyId === "No Company ID!") {
      const cid = UserContextInfo.activeCompanyId();
      companyId = cid && cid !== "No Company ID!" ? cid : null;
    }
    if (!companyId) {
      return { created: false, skipped: true, reason: "No active company" };
    }

    const exists = await this.hasExistingPlatform(companyId);
    if (exists) {
      return { created: false, skipped: true, reason: "Platform already exists" };
    }

    notify(i18n.t("kubernetesPlatform.devProvision.fetchingCluster"));
    const empty = await dataService.queries.createEmptyKubernetesPlatform(companyId);
    if (empty === BackEndConsts.BACKEND_RESPONSE_ERROR || !empty) {
      return { created: false, error: "Failed to fetch empty platform" };
    }

    notify(i18n.t("kubernetesPlatform.devProvision.detectingProvider"));
    let infraproviderid = null;
    try {
      const infras = await dataService.queries.getLainotikInfraProviders();
      if (infras && Array.isArray(infras)) {
        const local = infras.find(p => p.local === true) || infras[0];
        infraproviderid = local ? local.id : null;
      }
      if (!infraproviderid) infraproviderid = empty.infraproviderid || null;
    } catch (e) {
      infraproviderid = empty.infraproviderid || null;
    }

    let planid = null;
    try {
      const plans = await dataService.queries.getAllPlansData();
      if (plans && Array.isArray(plans)) {
        const minikube = plans.find(p => p.name === 'minikube');
        const fallback = plans.find(p => p.name === 'playground') || plans[0];
        planid = minikube ? minikube.id : (fallback ? fallback.id : null);
      }
      if (!planid) planid = empty.planid || null;
    } catch (e) {
      planid = empty.planid || null;
    }

    notify(i18n.t("kubernetesPlatform.devProvision.startingMasterEntities"));
    await dataService.mutations.setupMasterEntities();

    notify(i18n.t("kubernetesPlatform.devProvision.creatingPlatform"));
    const resp = await dataService.mutations.createPlatform(
      empty.id,
      companyId,
      "LOCAL-DEV",
      "https://localhost:6443",
      null,
      null,
      infraproviderid,
      planid,
      !!empty.lainotikmonitoring
    );

    if (resp === BackEndConsts.BACKEND_RESPONSE_ERROR || !resp?.kubernetesplatform) {
      return { created: false, error: "Platform creation failed" };
    }

    const k = resp.kubernetesplatform;
    updateActivePlatformLS(
      k.id,
      k.name,
      k.deploymentstateinfo?.codeinfolevel,
      k.deploymentstateinfo?.codeinfo,
      k.clsaddmon,
      k.connectionstate,
      resp.response?.messageinfo?.message || ""
    );

    try {
      notify(i18n.t("kubernetesPlatform.devProvision.activatingServices"));
      await dataService.mutations.activateK8ServicesAndApps(k.id);
      await this.ensureComponentsStorage(k.id, onProgress);
      await this.deployDevSampleApps(k.id, onProgress);
    } catch (e) {
      console.warn("RapidProvision: Error during post-cluster bootstrap", e);
    }

    return { created: true, platform: k, response: resp.response };
  }
};

export default devProvision;
